/**
 * Single source of truth for per-row worksheet measurement state (AP-764).
 *
 * `resolveMeasurementRows` joins the template labels, the report's SR/manual
 * measurements and the AI variants into one `MeasurementRowModel` per
 * canonical. Everything a row "is" — displayed value, source marker,
 * annotation target, drawing tool, range — is derived here, once,
 * and consumed by both the panel (display) and the click handlers (behavior),
 * so the two can never disagree.
 *
 * Pure functions only: no hooks, no store access. Callers memoize the result.
 */

import type { AIMeasurement } from '@/models/aiScan';
import type { Measurement } from '@/models/report';
import type { MeasurementGroup, MeasurementLabel } from '@/models/template';
import {
  canUseTool,
  unavailableHintFor,
  type MeasurementToolType,
  type SelectedImageTools,
} from '@/state/slices/measurementSession';
import {
  deriveDivergence,
  type DivergenceState,
} from '@/features/report/derived/divergence';
import {
  getToolTypeFromBackendTool,
  resolveMeasurementTool,
} from '@/utils/measurementToolMapping';
import {
  formatNormalRange,
  getRangeDirection,
  type RangeDirection,
} from '@/shared/utils/formatNormalRange';
import { getRangeSource, type RangeSource } from '@/shared/utils/rangeSource';
import type { ReferenceRanges } from '@/shared/utils/referenceRangeRules';
import { ageFallbackNote } from './rangeNote';

/** Relative tolerance for AI-vs-SR agreement: |a - b| / max(|a|, |b|).
 *  Fallback only — used when the clinical condition cannot be classified
 *  (see resolveConcordance). */
export const AI_AGREEMENT_THRESHOLD = 0.2;

export function withinAIAgreementThreshold(a: number, b: number): boolean {
  const denom = Math.max(Math.abs(a), Math.abs(b));
  if (denom === 0) return a === b;
  return Math.abs(a - b) / denom <= AI_AGREEMENT_THRESHOLD;
}

/** AI-vs-SR concordance (§3.2, AP-776): judged on the CONDITION, not the raw
 *  numbers. When the patient's normal band is resolvable (reference ranges +
 *  sex known), classify each reading as high / low / normal and disagree iff
 *  the classifications differ — SR "normal" vs AI "high" conflicts even when
 *  the numbers sit close; SR and AI both "high" agree even when far apart.
 *  Only when the condition cannot be determined (no band for this sex, or no
 *  sex) fall back to the relative-difference threshold. */
export function resolveConcordance(
  srValue: number,
  aiValue: number,
  referenceRanges: ReferenceRanges | null | undefined,
  sex: string | null | undefined,
  age?: number | null
): 'agree' | 'disagree' {
  if (formatNormalRange(referenceRanges, sex, age) !== null) {
    const srCondition = getRangeDirection(referenceRanges, sex, srValue, age);
    const aiCondition = getRangeDirection(referenceRanges, sex, aiValue, age);
    return srCondition === aiCondition ? 'agree' : 'disagree';
  }
  return withinAIAgreementThreshold(srValue, aiValue) ? 'agree' : 'disagree';
}

/** Pick which of two AI measurements sharing a canonical to display.
 *
 *  EchoMeasure can measure one field several ways (LV EF from biplane, A4C and
 *  A2C; LA area from six view/phase combinations), and they all arrive under the
 *  same canonical. The backend ranks them via `priority` but never filters —
 *  the viewer draws every variant's annotation — so the choice is ours:
 *  lowest priority wins, unranked sorts last, `privateCode` breaks ties.
 *
 *  The tiebreak is not cosmetic. AI measurements are deleted and re-created on
 *  every report refetch, so an all-unranked group ordered by payload alone could
 *  show a different value after a refetch with nothing explaining the change. */
export function preferAIVariant(
  current: AIMeasurement | undefined,
  candidate: AIMeasurement
): AIMeasurement {
  if (!current) return candidate;
  const rank = (m: AIMeasurement): [number, string] => [
    m.priority ?? Number.MAX_SAFE_INTEGER,
    m.privateCode ?? '',
  ];
  const [currentPriority, currentCode] = rank(current);
  const [candidatePriority, candidateCode] = rank(candidate);
  if (candidatePriority !== currentPriority)
    return candidatePriority < currentPriority ? candidate : current;
  return candidateCode < currentCode ? candidate : current;
}

/** The two roles an AI variant can play for a canonical.
 *
 *  `display` is the best-ranked variant overall — its value/unit are what the
 *  worksheet shows. `annotated` is the best-ranked variant that actually has
 *  an annotation — what the pencil and click-navigation use. They differ
 *  exactly when a computed variant (biplane LVEDV, no annotation) outranks the
 *  traced A4C/A2C variants that carry the geometry. */
export interface AIVariantRoles {
  display: AIMeasurement;
  annotated: AIMeasurement | null;
}

export function selectAIVariants(
  aiMeasurements: AIMeasurement[]
): Map<string, AIVariantRoles> {
  const display = new Map<string, AIMeasurement>();
  const annotated = new Map<string, AIMeasurement>();
  aiMeasurements.forEach((ai) => {
    if (!ai.canonicalMeasurement || ai.isDeleted) return;
    // An annotation is NOT required for display: derived values (LVEDV, LV EF,
    // …) are computed rather than traced, so they never have one.
    display.set(
      ai.canonicalMeasurement,
      preferAIVariant(display.get(ai.canonicalMeasurement), ai)
    );
    if (ai.annotation) {
      annotated.set(
        ai.canonicalMeasurement,
        preferAIVariant(annotated.get(ai.canonicalMeasurement), ai)
      );
    }
  });
  const roles = new Map<string, AIVariantRoles>();
  display.forEach((ai, canonical) => {
    roles.set(canonical, {
      display: ai,
      annotated: annotated.get(canonical) ?? null,
    });
  });
  return roles;
}

/** Swap canonical measurement keys in a derived formula for their worksheet
 *  labels so the tooltip reads "MV E / MV A" instead of "MV_E / MV_A".
 *  Identifier-shaped tokens are looked up; operators/numbers pass through, and
 *  unknown keys (a function name, a key without a label) fall back to raw. */
export function humanizeFormula(
  formula: string,
  labelByKey: Map<string, string>
): string {
  return formula.replace(
    /[A-Za-z_][A-Za-z0-9_]*/g,
    (token) => labelByKey.get(token) ?? token
  );
}

/** Source state for the row (AP-764 §3.1/§3.2) — every valued row carries one.
 *  Concordance can only exist on an AI-sourced value (invariant I1) — the type
 *  makes the illegal combination unrepresentable. `sr` is an explicit source
 *  that renders no glyph (SR is the common case); `null` means the row has no
 *  value. `srValue` is the SR reading the concordance was judged against,
 *  surfaced in the disagree glyph's tooltip; null exactly when
 *  `indeterminable` (no SR). */
export type RowMarker =
  | {
      kind: 'ai';
      concordance: 'agree' | 'disagree' | 'indeterminable';
      srValue: number | null;
    }
  | { kind: 'manual' }
  | { kind: 'sr' }
  | null;

/** Annotated (AP-764 §3.3): 'has' = drawn, 'can' = drawable on the selected
 *  image, 'can-elsewhere' = its tool doesn't fit the selected image,
 *  null = no tool. */
export type RowAnnotated = 'has' | 'can' | 'can-elsewhere' | null;

/** Where the row's annotation (pencil + click-navigation target) comes from.
 *  Human annotations win over AI. Normalized across the two payload shapes
 *  (report `Annotation.dicomUuid` vs `AIAnnotation.dicom`). */
export interface RowAnnotationRef {
  origin: 'human' | 'ai';
  dicomUuid: string;
  frameNumber: number;
}

export interface MeasurementRowModel {
  canonical: string;
  /** Displayed value: manual/SR value, or the display AI variant's. */
  value: number | null;
  unit: string;
  marker: RowMarker;
  /** Non-null ⇒ the row has geometry somewhere (filled pencil, clickable). */
  annotation: RowAnnotationRef | null;
  /** Resolved FE drawing tool (row wins over label, null gates off). */
  tool: MeasurementToolType | null;
  /** Annotated state (§3.3) — drives the pencil and whether a click arms. */
  annotated: RowAnnotated;
  /** Hover text when 'can-elsewhere': which image the tool needs. */
  unavailableHint: string | null;
  /** Non-null ⇒ template-derived/indexed row: `fx` indicator, interactions
   *  frozen (no tool/pencil). Source and concordance resolve like any row.
   *  `formula` is humanized for display ('' when the template has none). */
  computed: { formula: string } | null;
  /** Range state (§3.4): 'high'/'low' = outside the band, 'normal' = inside a
   *  KNOWN band, 'unmeasured' = band known but no value to compare yet,
   *  null = no band for this sex. Distinguishes "in range" / "awaiting a
   *  value" / "no range data". */
  range: RangeDirection | 'normal' | 'unmeasured' | null;
  /** The formatted normal band for this patient's sex ("4.2 - 5.8", "< 5.9"),
   *  or null when unknown — shown in the range arrow's tooltip. */
  normalRange: string | null;
  /** Where the band comes from (guideline label, optional PDF link) — shown
   *  under the band in the range tooltip; null when the fixture has none. */
  source: RangeSource | null;
  /** Caveat shown under the band when the range is age-specific but the
   *  patient's age is unknown or outside every block, so the baseline block
   *  is in use (see referenceRangeRules). null when the band is exact. */
  rangeNote: string | null;
  /** AP-731 §3 — 'diverged' ⇒ carries the `≠` indicator; null ⇒ not computed. */
  divergence: DivergenceState;
  /** Formula result, full precision; null when unevaluable. For §4 only. */
  calculated: number | null;
  /** Operand keys that would not resolve — named in the hover text (R9). */
  missingOperands: string[];
}

export interface ResolveMeasurementRowsArgs {
  measurementGroups: MeasurementGroup[];
  /** The report's measurements (SR/manual rows), raw. */
  measurements: Measurement[];
  aiMeasurements: AIMeasurement[];
  sex?: string | null;
  /** Patient age in whole years at the study date; age-specific reference
   *  ranges need it (see shared/utils/patientAge). */
  age?: number | null;
  /** Tool families the selected image takes; null = no gating. */
  selectedImageTools?: SelectedImageTools | null;
}

/** Range state (§3.4): the direction when out of band; 'normal' inside a
 *  KNOWN band; 'unmeasured' when the band is known but there is no value;
 *  null when no band exists ("no range data" — the red dot). */
function resolveRangeState(
  referenceRanges: ReferenceRanges | undefined,
  sex: string | null | undefined,
  value: number | null,
  age?: number | null
): RangeDirection | 'normal' | 'unmeasured' | null {
  const direction = getRangeDirection(referenceRanges, sex, value, age);
  if (direction) return direction;
  if (formatNormalRange(referenceRanges, sex, age) === null) return null;
  return value !== null && value !== 0 ? 'normal' : 'unmeasured';
}

export function resolveMeasurementRows({
  measurementGroups,
  measurements,
  aiMeasurements,
  sex,
  age,
  selectedImageTools,
}: ResolveMeasurementRowsArgs): Map<string, MeasurementRowModel> {
  const aiRoles = selectAIVariants(aiMeasurements);

  // Last row wins per canonical, mirroring the historical record-building
  // reduce. Valued rows drive value/marker; the raw row (even valueless)
  // drives annotation and tool.
  const rowByCanonical = new Map<string, Measurement>();
  measurements.forEach((m) => {
    if (m.canonicalMeasurement) rowByCanonical.set(m.canonicalMeasurement, m);
  });

  // Formula operands and missing-input keys are canonicalMeasurementKeys (see
  // MeasurementLabel.formula), NOT canonicalMeasurement — key the map on the
  // former so tooltips humanize e.g. "TRMAX_VEL" → "TR max velocity".
  const labelByKey = new Map<string, string>();
  measurementGroups.forEach((g) =>
    g.measurementLabels.forEach((ml) => {
      if (ml.canonicalMeasurementKey)
        labelByKey.set(ml.canonicalMeasurementKey, ml.label);
    })
  );

  const models = new Map<string, MeasurementRowModel>();

  // Formulas read the values the reader sees (§2), so every row resolves in
  // pass 1 before pass 2 derives divergence.
  const valueBySlug = new Map<string, number>();
  const computedRows: { canonical: string; ml: MeasurementLabel }[] = [];

  measurementGroups.forEach((group) =>
    group.measurementLabels.forEach((ml) => {
      const canonical = ml.canonicalMeasurement;
      if (!canonical) return;

      const row = rowByCanonical.get(canonical);
      const srValue = row?.value ?? null;
      const hasSR = srValue !== null;
      const roles = aiRoles.get(canonical);
      const aiValue = roles?.display.value ?? null;
      const hasAI = aiValue !== null;

      // Template-driven derived/indexed detection: the label carries
      // the authoritative `type` and `formula`.
      const isComputed = ml.type === 'DERIVED' || ml.type === 'INDEXED';

      // A typed or drawn value is manual and outranks SR/AI — even once cleared,
      // the AI/SR readings stay disregarded (AP-731 R8).
      const isManual = row?.valueSource === 'MANUAL' || !!row?.annotation;

      // Value + marker (AP-764 §3.1/§3.2) — uniform for ALL rows, computed
      // included (a computed row is only special in its interactions, I3).
      // Every valued row resolves an explicit source:
      //   manual (drawn line OR typed value) → manual dot, reader value
      //   SR + AI         → ai sparkle, AI value, agree | disagree
      //   AI only         → ai sparkle, AI value, indeterminable
      //   SR only         → sr (renders nothing), SR value
      //   no value        → null
      let marker: RowMarker = hasSR ? { kind: 'sr' } : null;
      let value: number | null = hasSR ? srValue : null;
      let unit: string =
        (hasSR ? row?.unit : undefined) ?? roles?.display.unit ?? ml.unit ?? '';

      if (isManual) {
        // A cleared manual row has no value, so no marker (AP-764 realized types).
        marker = value !== null ? { kind: 'manual' } : null;
      } else if (hasAI) {
        // AI present → show and mark the AI value (preferential over SR).
        value = aiValue;
        unit = roles!.display.unit ?? unit;
        const concordance: 'agree' | 'disagree' | 'indeterminable' = hasSR
          ? resolveConcordance(
              srValue,
              aiValue,
              row?.referenceRanges as ReferenceRanges | undefined,
              sex,
              age
            )
          : 'indeterminable';
        marker = { kind: 'ai', concordance, srValue };
      }

      // Annotation target: human wins, best-ranked ANNOTATED AI variant as
      // fallback — deliberately not the display variant, which for volumes is
      // often the computed (annotation-less) biplane one. Computed rows are
      // frozen (I3): no pencil/navigation even if the payload carries geometry.
      let annotation: RowAnnotationRef | null = null;
      if (!isComputed) {
        if (row?.annotation) {
          annotation = {
            origin: 'human',
            dicomUuid: row.annotation.dicomUuid,
            frameNumber: row.annotation.frameNumber,
          };
        } else if (roles?.annotated) {
          annotation = {
            origin: 'ai',
            dicomUuid: roles.annotated.annotation!.dicom,
            frameNumber: roles.annotated.annotation!.frameNumber,
          };
        }
      }

      // Drawing tool: report row wins (null gates off), label as fallback.
      // Computed rows never arm a tool (I3), whatever the template says.
      const tool = isComputed
        ? null
        : getToolTypeFromBackendTool(
            resolveMeasurementTool(
              row ? (row.tool ?? null) : undefined,
              ml.tool
            )
          );

      // Only where the selected image takes the row's tool.
      let annotated: RowAnnotated = null;
      let unavailableHint: string | null = null;
      if (annotation) annotated = 'has';
      else if (tool && canUseTool(tool, selectedImageTools)) annotated = 'can';
      else if (tool && selectedImageTools) {
        annotated = 'can-elsewhere';
        unavailableHint = unavailableHintFor(tool, selectedImageTools);
      }

      // One resolution feeds both the display and the arithmetic (R8).
      if (ml.canonicalMeasurementKey && value !== null) {
        valueBySlug.set(ml.canonicalMeasurementKey, value);
      }
      if (isComputed) computedRows.push({ canonical, ml });

      models.set(canonical, {
        canonical,
        value,
        unit,
        marker,
        annotation,
        tool,
        annotated,
        unavailableHint,
        computed: isComputed
          ? {
              formula: ml.formula
                ? humanizeFormula(ml.formula, labelByKey)
                : '',
            }
          : null,
        range: resolveRangeState(
          row?.referenceRanges as ReferenceRanges | undefined,
          sex,
          value,
          age
        ),
        normalRange: formatNormalRange(
          row?.referenceRanges as ReferenceRanges | undefined,
          sex,
          age
        ),
        source: getRangeSource(
          row?.referenceRanges as ReferenceRanges | undefined
        ),
        rangeNote: ageFallbackNote(
          row?.referenceRanges as ReferenceRanges | undefined,
          sex,
          age
        ),
        // Pass 2 fills these; a non-computed row keeps null (I1).
        divergence: null,
        calculated: null,
        missingOperands: [],
      });
    })
  );

  computedRows.forEach(({ canonical, ml }) => {
    const model = models.get(canonical);
    if (!model) return;
    const { state, calculated, missing } = deriveDivergence(
      ml.formula,
      model.value,
      valueBySlug,
      // Same fallback MeasurementsPanel renders with, so rounding matches.
      ml.precision ?? 2
    );
    model.divergence = state;
    model.calculated = calculated;
    model.missingOperands = missing;
  });

  return models;
}
